/**
 * Strudel runner.
 *
 * Replaces the separate strudel.cc tab the pilot used, which is the whole point:
 * the tutor could not see the learner's pattern, so the adaptation sheet had to
 * teach around it ("paste me what you've got right now"). Now every run carries
 * the pattern automatically.
 *
 * **This runner deliberately does not lint the code.** It would be easy to check
 * for the mechanical gotchas the adaptation sheet lists — unbalanced brackets,
 * missing quotes, effects written inside the mini-notation string — and say so.
 * That would be a mistake: the sheet asks the *tutor* to proofread Socratically
 * ("count the brackets on that line — do the opens and closes match?"), and a
 * runner that silently explains the bug takes that moment away. The runner's job
 * is to report what happened; the tutor now sees the code and can do the rest.
 */

import type { Runner, RunResult } from "./types";
import { truncateDetail } from "./types";

/** The @strudel/web surface used here, so the adapter is testable without audio. */
export interface StrudelModule {
  initStrudel(options?: { prebake?: () => unknown }): void;
  evaluate(code: string): Promise<unknown> | unknown;
  hush(): void;
  samples(source: string): unknown;
  /**
   * Strudel's Web Audio context.
   *
   * Needed because a real browser starts an AudioContext **suspended** until the user
   * has interacted, and will not make a sound until it is resumed. Playwright's
   * Chromium disables that policy, so a headless test cannot see the failure — which
   * is why this is handled rather than assumed.
   */
  getAudioContext?: () => { state: string; resume: () => Promise<void> } | undefined;
}

export type StrudelLoader = () => Promise<StrudelModule>;

/**
 * Where the drum samples come from.
 *
 * @strudel/web ships no samples, and the adaptation sheet's first-sixty-seconds
 * example is `sound("bd sd")` — so something has to be loaded or the very first
 * thing a learner tries is silence.
 *
 * This is the one place the app depends on a third party at run time, which is at
 * odds with self-hosting Pyodide for exactly that reason. Self-hosting a curated
 * subset of dirt-samples is the fix; it needs a licensing check and a few MB of
 * audio committed or served, so it is deliberately left as a known gap rather
 * than done badly. The browser caches these after the first load, so a session
 * survives wifi dropping mid-way — it is a cold start on a bad connection that
 * suffers.
 */
export const SAMPLE_SOURCE = "github:tidalcycles/dirt-samples";

/**
 * Whether this page is allowed to evaluate a string as JavaScript.
 *
 * Strudel cannot work without it, and fails silently rather than loudly, so this is
 * asked directly instead of inferred from a successful-looking `evaluate`.
 */
export function evalAllowed(): boolean {
  try {
    // eslint-disable-next-line no-new-func
    return new Function("return 1")() === 1;
  } catch {
    return false;
  }
}

export const STRUDEL_STARTER = `// Press Play. Change one thing. Press Play again.
sound("bd sd")`;

export class StrudelRunner implements Runner {
  readonly id = "strudel";
  readonly language = "javascript";
  readonly runLabel = "Play";
  readonly starterCode = STRUDEL_STARTER;
  readonly canStop = true;

  private strudel: StrudelModule | null = null;
  private loading: Promise<void> | null = null;
  private playing = false;

  constructor(
    private readonly loadStrudel: StrudelLoader,
    private readonly sampleSource: string | null = SAMPLE_SOURCE,
  ) {}

  async load(onProgress?: (message: string) => void): Promise<void> {
    if (this.loading) return this.loading;
    this.loading = (async () => {
      onProgress?.("Loading sounds…");
      const strudel = await this.loadStrudel();
      // initStrudel puts every pattern function (sound, note, s, …) on globalThis
      // and wires up Web Audio. Browsers only allow audio to start after a user
      // gesture, which is why this runs on the first Play rather than at startup.
      strudel.initStrudel(
        this.sampleSource ? { prebake: () => strudel.samples(this.sampleSource as string) } : {},
      );
      this.strudel = strudel;
      onProgress?.("Ready");
    })();
    return this.loading;
  }

  async run(code: string): Promise<RunResult> {
    try {
      await this.load();
    } catch (error) {
      return {
        outcome: "error",
        detail: truncateDetail(
          `Could not start the sound engine: ${
            error instanceof Error ? error.message : String(error)
          }`,
        ),
      };
    }

    const strudel = this.strudel;
    if (!strudel) return { outcome: "error", detail: "the sound engine is not running" };

    // Strudel evaluates the learner's pattern as JavaScript, and it swallows the
    // error if the environment forbids that — so `evaluate` resolves happily and
    // nothing has happened. That is exactly how a Content-Security-Policy without
    // 'unsafe-eval' produced a week of silence reported as "playing". Checked here
    // rather than trusted, because silence with a green tick is the worst possible
    // failure for a learner: nothing to debug and no reason to doubt themselves.
    if (!evalAllowed()) {
      return {
        outcome: "error",
        detail:
          "The sound engine cannot run here: this page forbids evaluating code, which " +
          "is how Strudel turns a pattern into music. A facilitator needs to allow " +
          "'unsafe-eval' in the Content-Security-Policy — see deploy/Caddyfile.",
      };
    }

    // A browser will not start audio until the user has interacted with the page, and
    // a context created during an async load can still come up suspended. Resumed
    // here, inside the handler for the learner's own click on Play, which is the only
    // moment a browser will honour.
    const context = strudel.getAudioContext?.();
    if (context && context.state === "suspended") {
      try {
        await context.resume();
      } catch {
        /* reported below if it did not take */
      }
    }
    if (context && context.state === "suspended") {
      return {
        outcome: "error",
        detail:
          "Your browser is not letting this page play sound yet. Click anywhere on " +
          "the page and press Play again. If it keeps happening, check the tab is not " +
          "muted — some browsers mute a tab until you interact with it.",
      };
    }

    try {
      await strudel.evaluate(code);
      this.playing = true;
      // No stdout to report. "It played" is the whole result — in Strudel the
      // sound is the teacher, so what the learner heard is the interesting part
      // and only they can describe it.
      return { outcome: "played", detail: "The pattern evaluated and is playing." };
    } catch (error) {
      // The exceptions that do behave like Python errors: missing quotes around
      // mini-notation, unbalanced brackets, effects inside the string. Reported
      // verbatim, not explained — see the note at the top of this file.
      return {
        outcome: "error",
        detail: truncateDetail(error instanceof Error ? error.message : String(error)),
      };
    }
  }

  async stop(): Promise<void> {
    this.strudel?.hush();
    this.playing = false;
  }

  get isPlaying(): boolean {
    return this.playing;
  }

  dispose(): void {
    try {
      this.strudel?.hush();
    } catch {
      // Disposing during teardown should never throw.
    }
    this.playing = false;
  }
}

/** Production factory. The import is lazy: @strudel/web touches `window` on load. */
export function createStrudelRunner(sampleSource: string | null = SAMPLE_SOURCE): StrudelRunner {
  return new StrudelRunner(
    async () => (await import("@strudel/web")) as unknown as StrudelModule,
    sampleSource,
  );
}
